import { useEffect, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButtonGhostMini, Tooltip, Typography } from '@kc-console/common-ui';
import { useAddFavoriteService, useDeleteFavoriteService } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { SpriteImgIcon } from '@kc-console/shared-ui';
import IcFavoriteFill from '@kep/kcds-icons/IcFavoriteFill';
import IcFavoriteRegular from '@kep/kcds-icons/IcFavoriteRegular';

import { MAX_FAVORITE_SERVICE_COUNT, useRefinedFavoriteServiceState } from '../../../../hooks';
import { getServiceDisplayName } from '../../../../utils';
import ServiceBadgeList from './ServiceBadgeList';

import type { MouseEvent } from 'react';
import type { MegaMenuService } from '@kc-console/context';

interface Props {
  open: boolean;
  service: MegaMenuService & { isFavorite?: boolean };
}

const AllServiceMenuItem = ({ service, open }: Props) => {
  const { t } = useTranslation();
  const { name, keyword, isFavorite } = service;
  const displayName = getServiceDisplayName(keyword, name);
  const { favoriteServices = [] } = useRefinedFavoriteServiceState();

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');

  const { mutate: addFavoriteService } = useAddFavoriteService();
  const { mutate: deleteFavoriteService } = useDeleteFavoriteService();

  const handleClickFavorite = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFavorite) {
      deleteFavoriteService(service);
      setTooltipMessage(t('layout:favorite.toast.removed'));
    } else if (favoriteServices.length >= MAX_FAVORITE_SERVICE_COUNT) {
      setTooltipMessage(
        t('layout:favorite.toast.max_limit', { maxCount: MAX_FAVORITE_SERVICE_COUNT }),
      );
    } else {
      addFavoriteService(service);
      setTooltipMessage(t('layout:favorite.toast.added'));
    }

    setShowTooltip(true);
  };

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => setShowTooltip(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  return (
    <MenuItemContainer>
      <ServiceIconWrapper>
        <SpriteImgIcon fontSize={20} id={keyword} category={'BeyondServiceRegular'} />
      </ServiceIconWrapper>
      <ServiceNameContainer>
        <ServiceNameText variant="label/regular/md">{displayName}</ServiceNameText>
        <ServiceBadgeList badges={service.badges} />
        <FavoriteIconWrapper visible={open || !!isFavorite}>
          <Tooltip message={showTooltip ? tooltipMessage : ''} placement="top" arrow>
            <FavoriteIconButton size="md" onClick={handleClickFavorite}>
              {isFavorite ? <IcFavoriteFill /> : <IcFavoriteRegular />}
            </FavoriteIconButton>
          </Tooltip>
        </FavoriteIconWrapper>
      </ServiceNameContainer>
    </MenuItemContainer>
  );
};

const MenuItemContainer = styled.div`
  display: flex;
  align-items: flex-start;
  width: 300px;
  padding: 5.5px 8px;
  gap: 8px;
`;

const ServiceIconWrapper = styled.div`
  height: 21px;
  display: flex;
  align-items: center;
`;

const ServiceNameContainer = styled.div``;

const ServiceNameText = styled(Typography)`
  white-space: pre-line;
`;

const FavoriteIconWrapper = styled.div<{ visible: boolean }>`
  vertical-align: bottom;
  margin-left: 4px;
  height: 21px;
  display: inline-flex;
  align-items: center;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`;

const FavoriteIconButton = styled(IconButtonGhostMini)(
  ({ theme }) => css`
    font-size: 12px;
    color: ${theme.color['icon/default']};
  `,
);

export default AllServiceMenuItem;
